/**
 * sessionStorage-backed "last picked site" for New Department.
 *
 * Lifetime: browser `sessionStorage` — per tab, gone when the tab closes.
 * Keyed by tenant + user so a remembered site never leaks across users or tenants
 * (including org switch and LabLogs admin impersonation in the same tab).
 * Cleared on every Login page load, so logout / timeout → login starts blank.
 * Known limitation: an SSO admin-kick (`IdpSessionGuard`) re-authenticates through the IdP
 * without rendering the Login page, so the same user in the same tab keeps their last pick.
 *
 * REBUILD-386 / JW 384#2
 */

export const NEW_DEPARTMENT_SITE_STORAGE_PREFIX = 'ell.newDepartmentSite.v1'

export const newDepartmentSiteStorageKey = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): string =>
  `${NEW_DEPARTMENT_SITE_STORAGE_PREFIX}:tenant:${tenantId ?? 'unknown'}:user:${userId ?? 'unknown'}`

const isSessionStorageAvailable = (): boolean => typeof sessionStorage !== 'undefined'

const toPositiveId = (value: string | null): number | null => {
  if (!value) return null
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : null
}

export const readNewDepartmentSite = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): number | null => {
  if (tenantId == null || !isSessionStorageAvailable()) return null

  try {
    return toPositiveId(sessionStorage.getItem(newDepartmentSiteStorageKey(tenantId, userId)))
  } catch {
    return null
  }
}

/** `null` removes the entry. */
export const writeNewDepartmentSite = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
  siteId: number | null,
): void => {
  if (tenantId == null || !isSessionStorageAvailable()) return

  const storageKey = newDepartmentSiteStorageKey(tenantId, userId)
  try {
    if (siteId == null) {
      sessionStorage.removeItem(storageKey)
    } else {
      sessionStorage.setItem(storageKey, String(siteId))
    }
  } catch {
    // QuotaExceededError / SecurityError — persistence is best-effort.
  }
}

/** Removes every remembered New Department site in this tab, for any tenant/user. */
export const clearAllNewDepartmentSites = (): void => {
  if (!isSessionStorageAvailable()) return

  try {
    const prefixedKeys = Array.from({ length: sessionStorage.length }, (_, index) =>
      sessionStorage.key(index),
    ).filter((storageKey): storageKey is string =>
      Boolean(storageKey?.startsWith(`${NEW_DEPARTMENT_SITE_STORAGE_PREFIX}:`)),
    )
    prefixedKeys.forEach((storageKey) => sessionStorage.removeItem(storageKey))
  } catch {
    // SecurityError — clearing is best-effort.
  }
}
